import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import { useSpec } from '../../contexts/SpecContext';
import { FalsePositiveModal } from './FalsePositiveModal';
import { FixSuggestionsTab } from './FixSuggestionsTab';
import { OverviewTab } from './OverviewTab';
import { SimilarVulnerabilitiesModal } from './SimilarVulnerabilitiesModal';
import styles from './VulnerabilityDetail.module.css';

export const VulnerabilityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeSpec } = useSpec();

  const [vulnerability, setVulnerability] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [linkedTest, setLinkedTest] = useState<any>(null);
  const [linkedHypothesis, setLinkedHypothesis] = useState<any>(null);
  const [initialFixSuggestions, setInitialFixSuggestions] = useState<any[]>([]);

  // Modal state
  const [showFPModal, setShowFPModal] = useState(false);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [similarVulns, setSimilarVulns] = useState<any[]>([]);
  const [isFindingSimilar, setIsFindingSimilar] = useState(false);
  const [fpContext, setFpContext] = useState({ reason: '', summary: '' });

  const loadVulnerability = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/vulnerabilities/${id}`;
      if (activeSpec?.id) {
        url += `?schema_id=${activeSpec.id}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        setVulnerability(result.data);

        if (result.data.ai_fix_suggestions) {
          try {
            const suggestions = JSON.parse(result.data.ai_fix_suggestions);
            setInitialFixSuggestions(Array.isArray(suggestions) ? suggestions : []);
          } catch (e) {
            console.error('Error parsing fix suggestions:', e);
          }
        }
      } else {
        toast.error('Failed to load vulnerability');
        navigate('/vulnerabilities');
      }
    } catch (error) {
      console.error('Error loading vulnerability:', error);
      toast.error('Failed to load vulnerability');
      navigate('/vulnerabilities');
    } finally {
      setLoading(false);
    }
  }, [id, activeSpec, navigate]);

  const loadLinkedData = useCallback(async () => {
    try {
      let url = `${API_BASE_URL}/api/vulnerabilities/${id}/linked`;
      if (activeSpec?.id) {
        url += `?schema_id=${activeSpec.id}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        setLinkedTest(result.test);
        setLinkedHypothesis(result.hypothesis);
      }
    } catch (error) {
      console.error('Error loading linked data:', error);
    }
  }, [id, activeSpec]);

  useEffect(() => {
    loadVulnerability();
  }, [loadVulnerability]);

  useEffect(() => {
    if (id) {
      loadLinkedData();
    }
  }, [id, loadLinkedData]);

  const handleFindSimilar = async (reason: string, summary: string) => {
    setFpContext({ reason, summary });
    setIsFindingSimilar(true);
    setShowSimilarModal(true);
    setSimilarVulns([]);

    try {
      let url = `${API_BASE_URL}/api/vulnerabilities/${id}/find-similar`;
      if (activeSpec?.id) url += `?schema_id=${activeSpec.id}`;
      const response = await fetch(url, {
        method: 'POST',
      });

      if (response.ok) {
        const result = await response.json();
        setSimilarVulns(result.data.similar);
        if (result.data.similar.length === 0) {
          toast.info('No similar vulnerabilities found');
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to find similar vulnerabilities');
      }
    } catch (error) {
      console.error('Error finding similar vulnerabilities:', error);
      toast.error('Failed to find similar vulnerabilities');
    } finally {
      setIsFindingSimilar(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Loading...</h3>
          <p>Please wait while we load the vulnerability details</p>
        </div>
      </div>
    );
  }

  if (!vulnerability) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Vulnerability not found</h3>
          <p>The requested vulnerability could not be found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/vulnerabilities')}>
          ← Back to Vulnerabilities
        </button>

        <div className={styles.titleRow}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{vulnerability.title}</h1>
            <div className={styles.vulnId}>{vulnerability.id}</div>
            <div className={styles.badgeRow}>
              <span className={styles.severityBadge} data-severity={vulnerability.severity?.toLowerCase()}>
                {vulnerability.severity}
              </span>
              <span className={styles.statusBadge} data-status={vulnerability.status?.toLowerCase()}>
                {vulnerability.status?.replace('_', ' ')}
              </span>
            </div>
          </div>

          <div className={styles.actionBar}>
            {!vulnerability.is_false_positive && (
              <button className={styles.actionButton} data-variant="warning" onClick={() => setShowFPModal(true)}>
                Mark False Positive
              </button>
            )}
            <button className={styles.actionButton} data-variant="secondary" onClick={loadVulnerability}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className={styles.contentGrid}>
        <div className={styles.tabContainer}>
          <div className={styles.tabList}>
            <button
              className={styles.tab}
              data-active={activeTab === 'overview' ? 'true' : 'false'}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={styles.tab}
              data-active={activeTab === 'fix' ? 'true' : 'false'}
              onClick={() => setActiveTab('fix')}
            >
              Fix Suggestions
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'overview' && (
              <OverviewTab vulnerability={vulnerability} linkedTest={linkedTest} linkedHypothesis={linkedHypothesis} />
            )}

            {activeTab === 'fix' && (
              <FixSuggestionsTab
                vulnerabilityId={vulnerability.id}
                initialSuggestions={initialFixSuggestions}
                schemaId={activeSpec?.id}
              />
            )}
          </div>
        </div>
      </div>

      {showFPModal && (
        <FalsePositiveModal
          vulnerabilityId={vulnerability.id}
          schemaId={activeSpec?.id}
          onClose={() => setShowFPModal(false)}
          onSubmitted={() => {
            setShowFPModal(false);
            loadVulnerability();
          }}
          onFindSimilar={handleFindSimilar}
        />
      )}

      {showSimilarModal && (
        <SimilarVulnerabilitiesModal
          similarVulns={similarVulns}
          isLoading={isFindingSimilar}
          fpReason={fpContext.reason}
          fpSummary={fpContext.summary}
          onClose={() => setShowSimilarModal(false)}
        />
      )}
    </div>
  );
};
